import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { personsApi } from "@/api/counterparties";
import type {
  CreatePersonRequest,
  DedupCandidateDto,
  DedupCheckResultDto,
  PersonDetailDto,
  UpdatePersonRequest,
} from "@/types/counterparties";
import { Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import {
  PersonForm,
  EMPTY_PERSON_FORM,
  type PersonFormState,
} from "@/components/counterparties/PersonForm";
import { DedupCheckModal } from "@/components/counterparties/DedupCheckModal";
import {
  CriticalFieldEditConfirmModal,
  type CriticalFieldChange,
} from "@/components/counterparties/CriticalFieldEditConfirmModal";
import { invalidateCounterpartyReads, personKeys } from "../counterpartyKeys";

// PersonEditPage handles both /counterparties/persons/new and
// /counterparties/persons/:id/edit. Convention matches BrokerEditPage —
// a dedicated edit page rather than a modal so the form can grow.

export function PersonEditPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState<PersonFormState>(EMPTY_PERSON_FORM);
  const [editingSsn, setEditingSsn] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupResult, setDedupResult] = useState<DedupCheckResultDto | null>(null);
  const [confirmCritical, setConfirmCritical] = useState<{
    changes: CriticalFieldChange[];
    onConfirm: () => void;
  } | null>(null);

  const existing = useQuery({
    queryKey: personKeys.detail(id),
    queryFn: () => personsApi.get(id!),
    enabled: editing,
  });

  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Persons", href: "/counterparties/persons" },
            { label: existing.data.displayName, href: `/counterparties/persons/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Persons", href: "/counterparties/persons" },
          { label: "New person" },
        ],
  );

  useEffect(() => {
    if (existing.data) {
      setForm(personDetailToFormState(existing.data));
      setEditingSsn(false);
      setEditingBank(false);
    }
  }, [existing.data]);

  const create = useMutation({
    mutationFn: (body: CreatePersonRequest) => personsApi.create(body),
    onSuccess: (created) => {
      invalidateCounterpartyReads(queryClient, "person");
      toast.success(`Person ${created.displayName} created.`);
      navigate(`/counterparties/persons/${created.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: (body: UpdatePersonRequest) => personsApi.update(id!, body),
    onSuccess: (updated) => {
      invalidateCounterpartyReads(queryClient, "person");
      toast.success("Person updated.");
      navigate(`/counterparties/persons/${updated.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const dedupRequest = useMemo(() => ({
    firstName: form.firstName || null,
    lastName: form.lastName || null,
    primaryEmail: form.primaryEmail || null,
    primaryPhone: form.primaryPhone || null,
    state: form.address.state || null,
    dateOfBirth: form.dateOfBirth ?? null,
    ssn: editingSsn || !editing ? form.ssn ?? null : null,
  }), [form, editingSsn, editing]);

  // Run the duplicate check up front. The modal is only shown when a
  // candidate match exists; with no match we create the record directly.
  const dedupPrecheck = useMutation({
    mutationFn: () => personsApi.dedupCheck(dedupRequest),
    onSuccess: (result) => {
      if (result.strength === "None") {
        create.mutate({ ...form, confirmCreateNew: true, createNewReason: null });
        return;
      }
      setDedupResult(result);
      setDedupOpen(true);
    },
    onError: (e: Error) => setError(e.message),
  });

  const onSave = () => {
    setError(null);
    if (!form.firstName || !form.lastName || !form.primaryEmail || !form.primaryPhone || !form.address.streetAddress1 || !form.address.city || !form.address.state || !form.address.zip) {
      setError("Please fill all required fields (name, email, phone, address).");
      return;
    }

    if (!editing) {
      // Create flow: dedup-check first.
      runCreate();
      return;
    }

    // Edit flow: detect critical-field changes; confirm before save.
    const changes = detectCriticalFieldChanges(existing.data, form, editingSsn);
    if (changes.length > 0) {
      setConfirmCritical({
        changes,
        onConfirm: () => {
          setConfirmCritical(null);
          submitUpdate();
        },
      });
      return;
    }
    submitUpdate();
  };

  const runCreate = (confirmCreateNew = false, reason: string | null = null) => {
    if (!confirmCreateNew) {
      dedupPrecheck.mutate();
      return;
    }
    const body: CreatePersonRequest = {
      ...form,
      confirmCreateNew,
      createNewReason: reason,
    };
    create.mutate(body);
  };

  const submitUpdate = () => {
    if (!existing.data) return;
    const body: UpdatePersonRequest = {
      ...form,
      status: existing.data.status,
    };
    update.mutate(body);
  };

  const isPending = create.isPending || update.isPending || dedupPrecheck.isPending;
  const requiredComplete =
    !!form.firstName &&
    !!form.lastName &&
    !!form.primaryEmail &&
    !!form.primaryPhone &&
    !!form.address.streetAddress1 &&
    !!form.address.city &&
    !!form.address.state &&
    !!form.address.zip;

  return (
    <>
      <PageHeader
        eyebrow="Counterparties · Person"
        title={editing ? "Edit person" : "New person"}
        description={editing ? "Edit identity, contact, role-specific, and banking details. Critical-field changes are confirmed." : undefined}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button size="sm" onClick={onSave} loading={isPending} disabled={!requiredComplete}>
              <Save size={14} strokeWidth={1.5} aria-hidden /> {editing ? "Save changes" : "Save Person"}
            </Button>
          </>
        }
      />

      {error && (
        <div className="bg-status-error/10 border-l-[3px] border-status-error text-small text-status-error p-space-3 rounded-md mb-space-3" role="alert">
          {error}
        </div>
      )}

      <PersonForm
        value={form}
        onChange={setForm}
        ssnMasked={existing.data?.ssnMasked}
        bankRoutingMasked={existing.data?.bankRoutingNumberMasked}
        bankAccountMasked={existing.data?.bankAccountNumberMasked}
        editingSsn={editingSsn}
        onEditSsn={() => setEditingSsn(true)}
        editingBank={editingBank}
        onEditBank={() => setEditingBank(true)}
      />

      <DedupCheckModal
        open={dedupOpen}
        kind="Person"
        request={dedupRequest}
        result={dedupResult ?? undefined}
        onLinkExisting={(c: DedupCandidateDto) => {
          setDedupOpen(false);
          toast.info(`Linked to existing person ${c.displayName}.`);
          navigate(`/counterparties/persons/${c.id}`);
        }}
        onCreateNew={(reason) => {
          setDedupOpen(false);
          runCreate(true, reason || null);
        }}
        onCancel={() => {
          setDedupOpen(false);
          setDedupResult(null);
        }}
      />

      {confirmCritical && (
        <CriticalFieldEditConfirmModal
          open
          changes={confirmCritical.changes}
          onConfirm={confirmCritical.onConfirm}
          onCancel={() => setConfirmCritical(null)}
        />
      )}
    </>
  );
}

function personDetailToFormState(p: PersonDetailDto): PersonFormState {
  return {
    firstName: p.firstName,
    middleName: p.middleName ?? "",
    lastName: p.lastName,
    suffix: p.suffix ?? "",
    dateOfBirth: p.dateOfBirth ?? null,
    // Cleartext SSN never echoed back to the client; the form starts blank
    // so saving without explicit edit submits null and the server
    // preserves the existing hash.
    ssn: null,
    usCitizen: p.usCitizen ?? null,
    maritalStatus: p.maritalStatus ?? null,
    primaryEmail: p.primaryEmail,
    secondaryEmail: p.secondaryEmail ?? "",
    primaryPhone: p.primaryPhone,
    primaryPhoneType: p.primaryPhoneType ?? null,
    secondaryPhone: p.secondaryPhone ?? "",
    preferredContactMethod: p.preferredContactMethod ?? null,
    address: p.address,
    mailingAddress: p.mailingAddress ?? null,
    industryExperienceYears: p.industryExperienceYears ?? null,
    estimatedNetWorth: p.estimatedNetWorth ?? null,
    estimatedLiquidity: p.estimatedLiquidity ?? null,
    preliminaryCreditScore: p.preliminaryCreditScore ?? null,
    preliminaryCreditScoreSource: p.preliminaryCreditScoreSource ?? null,
    equityInjectionSource: p.equityInjectionSource ?? "",
    accreditedInvestorStatus: p.accreditedInvestorStatus ?? null,
    accreditedInvestorBasis: p.accreditedInvestorBasis ?? null,
    taxFilingStatus: p.taxFilingStatus ?? null,
    originalSaleDate: p.originalSaleDate ?? null,
    originalSaleBasisInfo: p.originalSaleBasisInfo ?? "",
    bankACHName: p.bankACHName ?? "",
    bankRoutingNumber: null,
    bankAccountNumber: null,
    bankAccountType: p.bankAccountType ?? null,
    tags: p.tags,
  };
}

function detectCriticalFieldChanges(
  before: PersonDetailDto | undefined,
  after: PersonFormState,
  editingSsn: boolean,
): CriticalFieldChange[] {
  if (!before) return [];
  const changes: CriticalFieldChange[] = [];
  if (before.dateOfBirth !== (after.dateOfBirth ?? null)) {
    changes.push({
      field: "Date of birth",
      before: before.dateOfBirth ?? "—",
      after: after.dateOfBirth ?? "—",
    });
  }
  if (editingSsn && (after.ssn ?? "").length > 0) {
    changes.push({
      field: "SSN",
      before: before.ssnMasked ?? "—",
      after: "***-**-" + (after.ssn ?? "").slice(-4),
    });
  }
  if ((after.bankRoutingNumber ?? "").length > 0) {
    changes.push({
      field: "Bank routing number",
      before: before.bankRoutingNumberMasked ?? "—",
      after: "*****" + (after.bankRoutingNumber ?? "").slice(-4),
    });
  }
  if ((after.bankAccountNumber ?? "").length > 0) {
    changes.push({
      field: "Bank account number",
      before: before.bankAccountNumberMasked ?? "—",
      after: "*****" + (after.bankAccountNumber ?? "").slice(-4),
    });
  }
  return changes;
}

